import { ReactNode } from 'react';
import styles from './Alert.module.css';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  /**
   * The visual style of the alert.
   * Default: 'info'
   */
  variant?: AlertVariant;
  /**
   * Optional title to display in the alert.
   * When provided, the icon appears to the left of the title.
   */
  title?: string;
  /**
   * The content to display inside the alert.
   */
  children: ReactNode;
  /**
   * Optional callback function when the close button is clicked.
   * If provided, the close button will render.
   */
  onClose?: () => void;
  /**
   * If true, renders the alert as a "Fixed Alert" (Alerta fija/TIE)
   * with the full-width container structure.
   */
  isFixed?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export const Alert = ({
  variant = 'info',
  title,
  children,
  onClose,
  isFixed = false,
  className = '',
}: AlertProps) => {
  // Map variants to specific icons, base classes, and text colors.
  // Backgrounds come from the kit CSS (.uc-alert.{variant}).
  const variantConfig: Record<
    AlertVariant,
    { icon: string; styleClass: string; textColor: string }
  > = {
    info: {
      icon: 'info',
      styleClass: 'info',
      textColor: 'var(--color-text-info)',
    },
    success: {
      icon: 'check_circle',
      styleClass: 'success',
      textColor: 'var(--color-text-success)',
    },
    warning: {
      icon: 'warning',
      styleClass: 'warning',
      textColor: 'var(--color-text-warning)',
    },
    error: {
      icon: 'cancel',
      styleClass: 'error',
      textColor: 'var(--color-text-error)',
    },
  };

  const config = variantConfig[variant];
  const iconName = config.icon;

  // Determine the core alert class
  // Fixed alerts use 'tie-{variant}', simple alerts use just '{variant}'
  const alertBaseClass = isFixed ? `tie-${variant}` : config.styleClass;
  const combinedClasses = `uc-alert ${alertBaseClass} ${className}`;

  const AlertContent = (
    <div className={combinedClasses}>
      <div className={`uc-alert_content ${styles.content}`}>
        {title ? (
          <div className={styles.withTitle}>
            <div className={styles.titleRow}>
              <i className="uc-icon icon-size--md" style={{ color: config.textColor }}>
                {iconName}
              </i>
              <span
                className={`p p-size--md bold ml-8 ${styles.title}`}
                style={{ color: config.textColor }}
              >
                {title}
              </span>
            </div>
            <div className={styles.body}>
              <span className="p p-size--md">{children}</span>
            </div>
          </div>
        ) : (
          <>
            <i className="uc-icon icon-size--md">{iconName}</i>
            <span className="p p-size--md bold ml-8">{children}</span>
          </>
        )}
      </div>

      {onClose && (
        <div
          className="uc-alert_close"
          onClick={onClose}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <i className="uc-icon icon-size--sm">close</i>
        </div>
      )}
    </div>
  );

  // Return specific wrapper structure for Fixed Alerts.
  //
  // Note: the kit defines no background for `.uc-alert.tie-*` (only `border: 0`
  // and the icon color), and `.tie-success` does not exist at all. In fixed mode
  // the background stays transparent — the kit expects the `#tie` wrapper with
  // `.tie-border-{variant}` to provide the top rule instead.
  // If a background is ever needed in fixed mode, solve it with the kit's markup,
  // not with an inline style.
  if (isFixed) {
    return (
      <div id="alerta-tie">
        <div id="tie" className={`tie-border-${variant}`}>
          <div className="container">{AlertContent}</div>
        </div>
      </div>
    );
  }

  return AlertContent;
};

export default Alert;
